import React from 'react';
import {
	Divider,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import ContainerBox from 'common/components/layout/ContainerBox';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getTranslation } from 'common/modules/translations';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization';
import { timeslotToString } from 'common/modules/delivery/utils';
import { OrderDeliveryService, PurchaseTypes } from 'common/types';
import DetailSection from './DetailSection';
import { getPricingString, notUndefined } from 'common/utils/common';
import { getFormattedAddress } from 'common/modules/atoms/addresses';
import { SELF_RETURN_CARRIER } from 'common/modules/delivery/constants';

interface Props {
	delivery: OrderDeliveryService;
}

const DeliveryDetails = (props: Props) => {
	const { t } = useTranslation();
	const { shopDateFormat } = useShopFormat();

	const { delivery } = props;

	const lang = useSelector(LanguageSelectors.language);
	const orderInfo = useSelector(RentalViewSelectors.orderInfo)!;
	const orderDeliveryService = useSelector(RentalViewSelectors.orderDelivery);
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone);
	const currency = useSelector(ShopSelectors.getShopCurrency);

	const isDeliveryCarrierSelected = !!delivery.to?.carrier?.name;
	const isPickupCarrierSelected = !!delivery.from?.carrier?.name;

	const orderHasPickup =
		!!delivery.from && !delivery.from?.disabled && orderInfo.purchaseType !== PurchaseTypes.sales;

	const isSelfReturn = !!delivery.from && delivery.from?.carrier?.id === SELF_RETURN_CARRIER.id;

	const getSelfReturnDateRange = () => {
		const timeslot = orderDeliveryService?.from?.timeslot;
		return !!timeslot
			? timeslotToString({
					timeslot,
					shopDateFormat,
					timezone: shopTimezone,
			  })
			: '';
	};

	const deliveryName = getTranslation(delivery.name, lang);
	const deliveryDate = delivery.to?.timeslot
		? [
				timeslotToString({
					timeslot: delivery.to.timeslot,
					timezone: shopTimezone,
					shopDateFormat,
				}),
		  ].filter(notUndefined)
		: undefined;
	const deliveryCarrier = [
		isDeliveryCarrierSelected
			? getTranslation(delivery.to?.carrier?.name, lang)
			: t('orderSummaryDialog.deliveryDetails.notSelected', 'Not selected'),
	];

	const deliveryTrackingId =
		orderInfo.services?.delivery?.to?.trackingId ?? delivery?.to?.trackingId;

	const pickupDate =
		delivery.from?.timeslot && orderInfo.returnTimeNext
			? [
					timeslotToString({
						timeslot: delivery.from.timeslot,
						timezone: shopTimezone,
						shopDateFormat,
					}),
			  ].filter(notUndefined)
			: undefined;

	const pickupCarrier = [
		isPickupCarrierSelected
			? getTranslation(delivery.from?.carrier?.name, lang)
			: t('orderSummaryDialog.deliveryDetails.notSelected', 'Not selected'),
	];

	const pickupAddress =
		orderHasPickup && !!delivery.from?.location.address
			? getFormattedAddress(delivery.from.location.address)
			: undefined;

	const pickupTrackingId =
		orderInfo.services?.delivery?.from?.trackingId ?? delivery?.from?.trackingId;

	const pickupAdditionalInfo =
		orderHasPickup && delivery?.from?.location.address?.details
			? delivery?.from?.location.address?.details
			: undefined;

	const deliveryAddress = delivery?.to?.location.address
		? getFormattedAddress(delivery.to.location.address)
		: undefined;
	const deliveryAdditionalInfo = delivery?.to?.location.address?.details
		? delivery.to.location.address.details
		: undefined;

	const deliveryDetailsItems = [
		{
			label: t('orderSummaryDialog.deliveryDetails.deliveryDate', 'Delivery date'),
			values: deliveryDate,
		},
		{
			label: t('delivery.carrier'),
			values: deliveryCarrier,
		},
		...(!!deliveryTrackingId
			? [{ label: t('delivery.trackingId'), values: [deliveryTrackingId] }]
			: []),
		{
			label: t('orderSummaryDialog.contactDetails.deliveryAddress', 'Delivery address'),
			values: [deliveryAddress, deliveryAdditionalInfo].filter(notUndefined),
		},
		...(orderHasPickup
			? [
					{
						label: t('orderSummaryDialog.deliveryDetails.pickupDate', 'Pickup date'),
						values: pickupDate,
					},
					{
						label: t('orderSummaryDialog.contactDetails.pickupAddress', 'Pickup address'),
						values: [pickupAddress, pickupAdditionalInfo].filter(notUndefined),
					},
					{
						label: t('delivery.carrier'),
						values: pickupCarrier,
					},
			  ]
			: []),
		...(isSelfReturn
			? [
					{
						label: t('orderSummaryDialog.deliveryDetails.selfPickup', 'Self-return'),
						values: [getSelfReturnDateRange()],
					},
			  ]
			: []),
		...(!!pickupTrackingId
			? [{ label: t('delivery.trackingId'), values: [pickupTrackingId] }]
			: []),
	]
		.filter(notUndefined)
		.filter((item) => notUndefined(item.values));

	return (
		<ContainerBox>
			<Typography mb={2} variant="h6">
				{deliveryName}
			</Typography>
			<DetailSection items={deliveryDetailsItems} />

			<Table
				sx={{
					'.MuiTableCell-root': {
						borderBottom: 'none',
						bgcolor: (theme) => theme.palette.background.paper,
						pl: 0,
					},
					outline: 'none',
					bgcolor: (theme) => theme.palette.background.paper,
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>
							<Typography variant="body2" color="secondary">
								{t('orderSummaryDialog.deliveryDetails.deliveryType', 'Delivery type')}
							</Typography>
						</TableCell>
						<TableCell align="right">
							<Typography variant="body2" color="secondary">
								{t('common:pricing.priceWithCurrency', {
									currency: currency.displayAs,
									defaultValue: 'Price ({{currency}})',
								})}
							</Typography>
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>
							<Typography variant="body1">{t('delivery.delivery')}</Typography>
						</TableCell>
						<TableCell align="right">
							<Typography variant="body1">
								{delivery?.to ? getPricingString(delivery.to?.pricing.total) : null}
							</Typography>
						</TableCell>
					</TableRow>
					{orderHasPickup && (
						<>
							<Divider variant="fullWidth" />
							<TableRow>
								<TableCell>
									<Typography variant="body1">{t('delivery.pickup')}</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="body1">
										{delivery.from ? getPricingString(delivery.from?.pricing.total) : null}
									</Typography>
								</TableCell>
							</TableRow>
						</>
					)}
				</TableBody>
			</Table>
		</ContainerBox>
	);
};

export default DeliveryDetails;
